<template>
  <layout width="428">
    <template slot="header"> {{ isNew ? 'Add new' : 'Edit' }} object </template>
    <validation-observer ref="form">
      <v-form ref="form" class="mx-4" :disabled="isLoading">
        <validation-provider v-slot="{ errors }" name="Name" rules="required">
          <v-text-field
            data-test-type="deviceName"
            class="mt-2 mb-2"
            v-model.trim="name"
            label="Name"
            clearable
            :error-messages="errors"
          ></v-text-field>
        </validation-provider>
        <v-select
          :menu-props="{ bottom: true, offsetY: true }"
          v-if="isNew && !isCollections"
          class="mt-2 mb-2"
          data-test-type="type"
          v-model="selectedType"
          label="Schema type"
          :items="types"
          @change="schemaId = ''"
          :disabled="!!entityType"
          item-text="name"
          item-value="id"
        ></v-select>
        <validation-provider
          v-if="isNew"
          v-slot="{ errors }"
          name="Schema"
          rules="required"
        >
          <v-autocomplete
            clearable
            data-test-schema="deviceSchema"
            v-model="schemaId"
            :items="schemas"
            item-text="name"
            item-value="id"
            label="Schema"
            class="ma-0"
            :error-messages="errors"
            :disabled="isLoadSchemas"
            :loading="isLoadSchemas"
          >
            <template v-slot:item="item">
              <div :data-test-schema="item.item.name">
                {{ item.item.name }}
              </div>
            </template>
          </v-autocomplete>
        </validation-provider>
        <div class="d-flex align-center mx-0" data-test="enabled">
          <div style="font-size: 16px">Enabled</div>
          <v-spacer />
          <v-switch
            data-test-switch="enabled"
            class="ma-0 py-3"
            hide-details
            :false-value="false"
            :true-value="true"
            v-model="enabled"
          ></v-switch>
        </div>
        <subheader>Description</subheader>
        <v-textarea
          data-test="description"
          v-model="description"
          no-resize
          rows="1"
          auto-grow
        ></v-textarea>
      </v-form>
    </validation-observer>
    <v-divider />
    <template #footer>
      <v-spacer />
      <v-btn text @click="close" data-test-action="deviceCloseAction">
        cancel
      </v-btn>
      <v-btn
        color="primary"
        data-test-action="deviceCreateOrEditAction"
        :loading="isLoading"
        text
        @click.stop="handleSave"
      >
        <template v-if="isNew">add</template>
        <template v-else>save</template>
      </v-btn>
    </template>
  </layout>
</template>

<script>
import Layout from '@/layouts/LayoutPopup';
import {
  computed,
  ref,
  onMounted,
  reactive,
  toRefs
} from '@vue/composition-api';

setInteractionMode('eager');

import { required } from 'vee-validate/dist/rules';
import {
  extend,
  ValidationObserver,
  ValidationProvider,
  setInteractionMode
} from 'vee-validate';

extend('required', {
  ...required,
  message: '{_field_} can not be empty'
});

import useAuth from '@/api/auth';
import { useLazyQuery, useMutation, useResult } from '@vue/apollo-composable';

import schema from '@/api/schemas/list.graphql';
import schemaDevice from '@/api/devices/list.graphql';
import objectItem from '@/api/objects/item.graphql';

export default {
  name: 'CreateObject',
  props: {
    entity: {
      type: [Object],
      required: true
    },
    isCollections: {
      type: Boolean,
      default: false
    },
    entityType: {
      type: String,
      default: ''
    },
    id: {
      type: String,
      default: ''
    },
    linkAfterCreation: {
      type: Boolean,
      default: false
    },
    loading: {
      type: Boolean,
      default: false
    },
    callbackAfter: {
      type: Function,
      default: () => {}
    }
  },
  components: {
    Layout,
    ValidationObserver,
    ValidationProvider
  },

  setup(props, { emit, root, refs }) {
    const controller = new window.AbortController();
    const { user, profile } = new useAuth();
    const localEntity = reactive({
      id: props.entity.id,
      schemaId: props.entity.schemaId,
      name: props.entity.name,
      enabled: true,
      description: props.entity.description
    });

    const selectedType = ref(props.entityType || 'DEVICE');

    const types = ref([
      {
        name: 'Device',
        id: 'DEVICE'
      },
      {
        name: 'Dataset',
        id: 'DATASET'
      },
      {
        name: 'Media',
        id: 'MEDIA'
      },
      {
        name: 'Application',
        id: 'APPLICATION'
      }
    ]);

    const { mutate: linkObjToObj } = useMutation(
      objectItem.connectObjectsToObject
    );

    const { mutate: createObject, loading: isCreatingObject } = useMutation(
      schemaDevice.save,
      {
        context: {
          fetchOptions: {
            signal: controller.signal
          }
        }
      }
    );

    const { mutate: updateObject, loading: isUpdatingObject } = useMutation(
      objectItem.update,
      {
        context: {
          fetchOptions: {
            signal: controller.signal
          }
        }
      }
    );

    const lazyQuery = useLazyQuery(
      schema.loadPure,
      {
        filter: {
          enabled: {
            equalTo: true
          },
          id: {},
          type: {}
        }
      },
      {
        fetchPolicy: 'network-only',
        nextFetchPolicy: 'network-only'
      }
    );

    const schemas = useResult(lazyQuery.result, [], data => {
      return data.schemata;
    });

    const isNew = computed(() => {
      return !localEntity.id;
    });

    function close() {
      if (isCreatingObject.value || isUpdatingObject.value) {
        controller.abort();
      }

      emit('close');
    }

    const createObjectHandler = () => {
      createObject({
        input: {
          object: {
            schemaId: localEntity.schemaId,
            name: localEntity.name,
            enabled: localEntity.enabled,
            description: localEntity.description,
            editorgroup: user.value.defaultEditorgroup
          }
        }
      }).then(({ data }) => {
        close();
        props.callbackAfter();

        if (props.linkAfterCreation) {
          linkObjToObj({
            input: {
              objectsToObject: {
                object1Id: props.id || root.$route.query?.nestedId || root.$route.params.id,
                object2Id: data.createObject.object.id
              }
            }
          }).then(() => {
            close();
          });
        } else {
          redirectToCard(data.createObject.object.id);
        }
      });
    };

    const updateObjectHandler = () => {
      updateObject({
        input: {
          id: props.entity.id,
          patch: {
            name: localEntity.name,
            description: localEntity.description,
            enabled: localEntity.enabled
          }
        }
      }).then(() => {
        close();
        props.callbackAfter();
      });
    };

    const redirectToCard = objectId => {
      let routeName = '';
      if (props.isCollections) {
        routeName = 'collectionsTableComponent';
      } else {
        switch (selectedType.value.toLowerCase()) {
          case 'dataset':
            routeName = 'datasetsTableComponent';
            break;
          case 'application':
            routeName = 'appsTableComponent';
            break;
          default:
            routeName = 'devicesTableComponent';
            break;
        }
      }

      root.$router.push({
        name: routeName,
        params: { id: objectId }
      });
    };

    function getCollection() {
      const customItemsProp = profile.value.objectProperties.find(
        item => item.key === 'menuSettingsCustomItems'
      );

      if (customItemsProp && customItemsProp.value.length) {
        return customItemsProp.value.find(
          item => item.id === root.$route.params.collectionId
        );
      }
      return null;
    }

    onMounted(() => {
      if (isNew.value) {
        lazyQuery.variables.value.filter.enabled = {
          equalTo: true
        };
        if (props.isCollections) {
          lazyQuery.variables.value.filter.id = {
            in: getCollection()?.schemas
          };
        } else {
          lazyQuery.variables.value.filter.type = {
            equalTo: selectedType
          };
        }
        lazyQuery.load();
        localEntity.enabled = true;
      } else {
        localEntity.enabled = props.entity.enabled;
      }
    });

    const handleSave = () => {
      refs.form.validate().then(success => {
        if (!success) {
          return;
        }

        if (isNew.value) {
          createObjectHandler();
        } else {
          updateObjectHandler();
        }
      });
    };

    return {
      ...toRefs(localEntity),
      selectedType,
      isNew,
      close,
      types,
      schemas,
      handleSave,
      isLoading: isCreatingObject || isUpdatingObject,
      isLoadSchemas: lazyQuery.loading
    };
  }
};
</script>
